import { getBaseRole } from '@/helpers/customRoles';
import {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import { flushSync } from 'react-dom'
import { Input, Tooltip } from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import cn from 'classnames'
import { useAsync } from 'react-use'
import { useSelector } from 'react-redux'
import { crmApi } from '@/api'
import { Field } from '@/api/types/crm/crm.types'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import { CrmFunnelStatusItem } from '@/api/types/crm/crmFunnels.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCRMContext } from '@pages/Admin/CRM/CRMContext'
import { selectProfile } from '@store/profile.reducer'
import {
  buildLeadPhoneFieldSettings,
  getLeadFieldValue,
} from '@pages/Admin/CRM/utils/leadFields'
import {
  MAIN_PROFILE_LEAD_FIELD_IDS,
  MAIN_PROFILE_PARENT_FIELD_IDS,
  pickMainProfileField,
} from '@pages/Admin/CRM/utils/mainProfileFields'
import Select from '../../../base/Select/Select'
import LeadField from '../../components/LeadField/LeadField'
import SelectNewStatus from '../../components/SelectNewStatus/SelectNewStatus'
import useFullLeadModalAccess from '../../hooks/useFullLeadModalAccess'
import { useLeadRefetch } from '../../LeadRefetchContext'
import LeadProfileModalBlock from '../../components/LeadProfileModalBlock/LeadProfileModalBlock'
import compactFieldClasses from '@pages/Admin/CRM/styles/compactLeadFields.module.css'

import VkLinkField from './VkLinkField'
import MessengerAccountLinkField from './MessengerAccountLinkField'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import {
  MAX_ACCOUNT_FIELD_UNIQUE_NAME,
  TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME,
  type MessengerIdentityFieldUniqueName,
} from '@pages/Admin/CRM/utils/messengerAccountFields'
import classes from './index.module.css'

const FUNNEL_NEXT_CONTACT_FIELD: Field = {
  id: -9001,
  unique_name: '__FUNNEL_NEXT_CONTACT_AT__',
  name: 'Дата следующего контакта',
  type: 'datetime',
  value_options: null,
  is_active: true,
  is_default: true,
  is_required: false,
  sorting: 9999,
  settings: {},
}

const getLeadFunnelId = (funnel: {
  id: number
  lead_funnel_id?: number
}): number => {
  const raw = funnel.lead_funnel_id ?? funnel.id
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : Number(funnel.id)
}

const getDealSelectorLabel = (funnel: {
  id: number
  lead_funnel_id?: number
  deal?: { id: number; name: string } | null
}): string => {
  const leadFunnelId = getLeadFunnelId(funnel)
  const dealName = funnel.deal?.name?.trim()
  return dealName || `Сделка #${leadFunnelId}`
}

type FunnelOption = {
  id: number
  name: string
  responsibleManagerId: number | null
}

type FunnelWithStatuses = FunnelOption & {
  statusIds: number[]
  statuses: CrmFunnelStatusItem[]
}

export type LeadWithFunnelData = LeadProfileLead

interface MainInfoBlockProps {
  lead?: LeadWithFunnelData
  fields: Field[]
  fieldsData: Record<string, string>
  updateFieldData: (uniqueNameField: string, value: string) => void
  selectedManagerId: number | null
  onSelectManager: (managerId: number | null) => void
  onClearManager?: () => void
  canEditManager: boolean
  showManager: boolean
  onStatusChanged?: () => void
  deferStatusCommit?: boolean
  onFunnelStatusSelectionSync?: (statusId: number | null) => void
  funnelNextContactDraftRef?: MutableRefObject<string>
  dealCommentDraftRef?: MutableRefObject<string>
  onOpenVkChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenMaxChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenTelegramChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  onOpenWazzupChatWithLead?: (
    leadId: number,
    options?: { conversationId?: number },
  ) => void
  identityFieldErrors?: Partial<Record<MessengerIdentityFieldUniqueName, string>>
  onClearIdentityFieldError?: (uniqueName: MessengerIdentityFieldUniqueName) => void
  /** Инкремент сбрасывает локальные черновики статуса / даты / комментария. */
  draftResetKey?: number
}

const MainInfoBlock: FC<MainInfoBlockProps> = ({
  lead,
  fields,
  fieldsData,
  updateFieldData,
  selectedManagerId,
  onSelectManager,
  onClearManager,
  canEditManager,
  showManager,
  onStatusChanged,
  deferStatusCommit = false,
  onFunnelStatusSelectionSync,
  funnelNextContactDraftRef,
  dealCommentDraftRef,
  onOpenVkChatWithLead,
  onOpenMaxChatWithLead,
  onOpenTelegramChatWithLead,
  onOpenWazzupChatWithLead,
  identityFieldErrors,
  draftResetKey = 0,
  onClearIdentityFieldError,
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)

  const { preloadingData, vkIntegration } = useCRMContext()
  const { providers: integrationProviders, isLoading: isIntegrationAvailabilityLoading } =
    useCrmIntegrationChatAvailability()
  const { managers } = preloadingData
  const { currentRole, ...access } = useFullLeadModalAccess()

  /** CRM `managers.id` текущего юзера (не путать с `profile.id` = user.id). */
  const currentManagerCrmId = useMemo(() => {
    const profileUserId = Number(profile?.id)
    if (!Number.isFinite(profileUserId) || profileUserId <= 0) return null

    const me = managers.find(
      (manager) => Number(manager?.user?.id) === profileUserId,
    )
    const id = me?.id != null ? Number(me.id) : NaN
    return Number.isFinite(id) && id > 0 ? id : null
  }, [managers, profile?.id])

  const {
    isRefetchingLeadCard,
    selectedFunnelId,
    selectFunnel,
    multipleDealsEnabled,
    selectedLeadFunnelId,
    selectLeadFunnel,
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
    committedComment,
    getCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
    leadCardSnapshot,
  } = useLeadRefetch()

  const [isFunnelSelectOpen, setIsFunnelSelectOpen] = useState(false)
  const [isDealSelectOpen, setIsDealSelectOpen] = useState(false)
  const funnelDropdownLockedRef = useRef(false)
  const dealDropdownLockedRef = useRef(false)
  const funnelDropdownUnlockTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  const dealDropdownUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const lockSelectDropdownClosed = (
    kind: 'funnel' | 'deal',
    setOpen: (open: boolean) => void,
  ) => {
    const lockedRef =
      kind === 'funnel' ? funnelDropdownLockedRef : dealDropdownLockedRef
    const timerRef =
      kind === 'funnel'
        ? funnelDropdownUnlockTimerRef
        : dealDropdownUnlockTimerRef

    lockedRef.current = true
    flushSync(() => {
      setOpen(false)
    })

    const active = document.activeElement
    if (active instanceof HTMLElement) {
      active.blur()
    }

    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
    }
    // Пока идут ререндеры refetch, antd пытается снова открыть дропдаун из-за фокуса.
    timerRef.current = setTimeout(() => {
      lockedRef.current = false
      timerRef.current = null
    }, 600)
  }

  const handleFunnelDropdownVisibleChange = (visible: boolean) => {
    if (visible && funnelDropdownLockedRef.current) return
    setIsFunnelSelectOpen(visible)
  }

  const handleDealDropdownVisibleChange = (visible: boolean) => {
    if (visible && dealDropdownLockedRef.current) return
    setIsDealSelectOpen(visible)
  }

  useEffect(() => {
    return () => {
      if (funnelDropdownUnlockTimerRef.current != null) {
        clearTimeout(funnelDropdownUnlockTimerRef.current)
      }
      if (dealDropdownUnlockTimerRef.current != null) {
        clearTimeout(dealDropdownUnlockTimerRef.current)
      }
    }
  }, [])

  const currentFunnels = useMemo(() => {
    const list = leadCardSnapshot?.current_funnels
    return Array.isArray(list) ? list : []
  }, [leadCardSnapshot])

  const dealOptions = useMemo(() => {
    const scoped =
      selectedFunnelId != null
        ? currentFunnels.filter(
            (funnel) => Number(funnel.funnel_id) === Number(selectedFunnelId),
          )
        : currentFunnels

    return scoped.map((funnel) => {
      const name = getDealSelectorLabel(funnel)
      const managerIdRaw =
        funnel.manager_id != null ? funnel.manager_id : funnel.manager?.id
      const managerId =
        managerIdRaw != null && Number.isFinite(Number(managerIdRaw))
          ? Number(managerIdRaw)
          : null
      const isMine =
        currentManagerCrmId != null &&
        managerId != null &&
        managerId === currentManagerCrmId

      return {
        value: getLeadFunnelId(funnel),
        title: name,
        label: isMine ? (
          <span className={classes.dealOptionLabel}>
            <span className={classes.dealOptionName}>{name}</span>
            <Tooltip
              title="Вы установлены менеджером по этой сделке"
              mouseEnterDelay={0.25}
              getPopupContainer={() => document.body}
            >
              <span className={classes.dealMineBadge}>моя</span>
            </Tooltip>
          </span>
        ) : (
          name
        ),
      }
    })
  }, [currentFunnels, selectedFunnelId, currentManagerCrmId])

  const leadManagerId = lead?.manager?.id ?? null
  const leadManagerName =
    lead?.manager && 'name' in lead.manager
      ? String(lead.manager.name)
      : lead?.manager?.fio ?? null

  const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  const sourceFunnels = useMemo<FunnelOption[]>(() => {
    const raw: FunnelOption[] = []

    if (lead?.funnels?.length) {
      for (const funnel of lead.funnels) {
        const id = Number(funnel.id)
        if (!Number.isFinite(id) || id <= 0) continue
        raw.push({
          id,
          name: funnel.name,
          responsibleManagerId: toNullableNumber(funnel.manager_id),
        })
      }
    } else if (
      lead?.funnel &&
      Number.isFinite(Number(lead.funnel.id)) &&
      Number(lead.funnel.id) > 0
    ) {
      raw.push({
        id: Number(lead.funnel.id),
        name: lead.funnel.name,
        responsibleManagerId: leadManagerId,
      })
    }

    // Несколько сделок в одной воронке не должны плодить опции селекта воронок.
    const uniqueById = new Map<number, FunnelOption>()
    for (const funnel of raw) {
      if (!uniqueById.has(funnel.id)) {
        uniqueById.set(funnel.id, funnel)
      }
    }
    return Array.from(uniqueById.values())
  }, [lead?.funnels, lead?.funnel, leadManagerId])

  const targetFunnelId = useMemo(() => {
    if (!sourceFunnels.length) return null
    const selected = sourceFunnels.find(
      (funnel) => funnel.id === selectedFunnelId,
    )
    if (selected) return selected.id

    const fromLead = toNullableNumber(lead?.currentFunnelId)
    if (
      fromLead !== null &&
      sourceFunnels.some((funnel) => funnel.id === fromLead)
    ) {
      return fromLead
    }

    return sourceFunnels[0].id
  }, [lead?.currentFunnelId, selectedFunnelId, sourceFunnels])

  const funnelStatusesState = useAsync(async () => {
    if (!targetFunnelId) {
      return new Map<number, CrmFunnelStatusItem[]>()
    }

    const response = await crmApi.funnels.statuses.getStatuses({
      funnel_id: targetFunnelId,
    })
    const statuses = response?.data?.data?.statuses ?? []
    return new Map<number, CrmFunnelStatusItem[]>([[targetFunnelId, statuses]])
  }, [targetFunnelId])

  useEffect(() => {
    if (!funnelStatusesState.error) return

    addAlert({
      title: 'Ошибка',
      text: 'Не удалось загрузить статусы воронок',
      type: 'error',
    })
  }, [addAlert, funnelStatusesState.error])

  const availableFunnels = useMemo<FunnelWithStatuses[]>(() => {
    const canViewAllFunnels = ['admin', 'head-of-sales'].includes(getBaseRole(currentRole))

    const statusesMap =
      funnelStatusesState.value ?? new Map<number, CrmFunnelStatusItem[]>()

    const hydratedFunnels = sourceFunnels.map((funnel) => {
      const statuses = statusesMap.get(funnel.id) ?? []

      return {
        ...funnel,
        statuses,
        statusIds: statuses.map((status) => status.id),
      }
    })

    if (canViewAllFunnels) {
      return hydratedFunnels
    }

    if (getBaseRole(currentRole) === 'sales-manager') {
      // Раньше список сужали по manager_id заявки = менеджер лида и добавляли только
      // `currentFunnelId`. После «Добавить воронку» / смены воронки остальные заявки
      // пропадали из селекта. В карточке показываем все воронки из `lead.funnels`.
      return hydratedFunnels
    }

    return hydratedFunnels
  }, [currentRole, funnelStatusesState.value, sourceFunnels])

  useEffect(() => {
    if (availableFunnels.length === 0) {
      // Не зовём selectFunnel(null): при multiple deals это роняет
      // выбранную сделку, а потом selectFunnel(id) ставит первую в воронке.
      return
    }

    const hasSelectedFunnel =
      selectedFunnelId !== null &&
      availableFunnels.some((funnel) => funnel.id === selectedFunnelId)

    if (!hasSelectedFunnel) {
      const fromLead = toNullableNumber(lead?.currentFunnelId)
      const nextFromLead =
        fromLead !== null &&
        availableFunnels.some((funnel) => funnel.id === fromLead)
          ? fromLead
          : null
      const nextFunnelId = nextFromLead ?? availableFunnels[0].id
      if (nextFunnelId !== selectedFunnelId) {
        selectFunnel(nextFunnelId)
      }
    }
  }, [availableFunnels, lead?.currentFunnelId, selectFunnel, selectedFunnelId])

  const selectedFunnel = useMemo(
    () =>
      availableFunnels.find((funnel) => funnel.id === selectedFunnelId) ?? null,
    [availableFunnels, selectedFunnelId],
  )
  const prevSelectedFunnelIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!lead) return
    if (!canEditManager) return

    const hasFunnelChanged =
      prevSelectedFunnelIdRef.current !== selectedFunnelId
    prevSelectedFunnelIdRef.current = selectedFunnelId

    if (!hasFunnelChanged) return

    if (selectedFunnelId === null) {
      if (selectedManagerId !== null) onSelectManager(null)
      return
    }

    onSelectManager(committedManagerId)
  }, [
    canEditManager,
    lead,
    selectedFunnelId,
    selectedManagerId,
    committedManagerId,
    onSelectManager,
  ])

  const managerOptions = useMemo(() => {
    const options = managers.map((manager) => ({
      value: manager.id,
      label: manager.fio || `ID ${manager.id}`,
    }))

    const hasLeadManager =
      leadManagerId !== null &&
      options.some((option) => option.value === leadManagerId)

    if (leadManagerId !== null && !hasLeadManager) {
      return [
        ...options,
        {
          value: leadManagerId,
          label: leadManagerName || `ID ${leadManagerId}`,
        },
      ]
    }

    return options
  }, [leadManagerId, leadManagerName, managers])

  const allowedStatusIds = selectedFunnel?.statusIds ?? []
  const selectedFunnelStatuses = selectedFunnel?.statuses ?? []
  const hasFunnels = availableFunnels.length > 0

  const [funnelNextContactValue, setFunnelNextContactValue] = useState('')
  const [dealCommentValue, setDealCommentValue] = useState('')
  const [dealCommentExpanded, setDealCommentExpanded] = useState(false)

  useEffect(() => {
    setFunnelNextContactValue(committedNextContactAt ?? '')
  }, [
    committedNextContactAt,
    selectedFunnelId,
    selectedLeadFunnelId,
    draftResetKey,
  ])

  useEffect(() => {
    if (funnelNextContactDraftRef) {
      funnelNextContactDraftRef.current = funnelNextContactValue
    }
  }, [funnelNextContactDraftRef, funnelNextContactValue])

  useEffect(() => {
    setDealCommentValue(committedComment ?? '')
    setDealCommentExpanded(false)
  }, [
    committedComment,
    selectedFunnelId,
    selectedLeadFunnelId,
    draftResetKey,
  ])

  useEffect(() => {
    if (dealCommentDraftRef) {
      dealCommentDraftRef.current = dealCommentValue
    }
  }, [dealCommentDraftRef, dealCommentValue])

  const isSelectedFunnelAvailable =
    hasFunnels &&
    selectedFunnelId !== null &&
    availableFunnels.some((funnel) => funnel.id === selectedFunnelId)

  /** Цвет левой полоски блока — как у текущего статуса по выбранной воронке */
  const funnelBundleAccentStatusColor = useMemo(() => {
    if (selectedFunnelId === null) return null

    const snap = getCardInFunnelSnapshot(
      selectedFunnelId,
      multipleDealsEnabled ? selectedLeadFunnelId : null,
    )
    if (snap) {
      const fromCurrent = snap.current_status?.color
      const fromLegacy = snap.status?.color
      const raw = fromCurrent ?? fromLegacy
      if (raw != null && String(raw).trim() !== '') return String(raw).trim()
    }

    if (
      committedCurrentStatusId != null &&
      selectedFunnelStatuses.length > 0
    ) {
      const row = selectedFunnelStatuses.find(
        (s) => s.id === committedCurrentStatusId,
      )
      if (row?.color && String(row.color).trim() !== '') {
        return String(row.color).trim()
      }
    }

    const leadFunnelId = toNullableNumber(lead?.currentFunnelId)
    const leadColor = (
      lead as { currentFunnelStatusColor?: string | null }
    )?.currentFunnelStatusColor
    if (
      leadColor != null &&
      String(leadColor).trim() !== '' &&
      leadFunnelId === selectedFunnelId
    ) {
      return String(leadColor).trim()
    }

    return null
  }, [
    selectedFunnelId,
    selectedLeadFunnelId,
    multipleDealsEnabled,
    getCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
    committedCurrentStatusId,
    selectedFunnelStatuses,
    lead,
  ])

  if (!lead) return null

  const renderMainProfileField = (
    field: Field,
    rowClassName: string,
    disabled = !access.canEditFields,
  ) => {
    const defaultValue = getLeadFieldValue(lead, field.unique_name) ?? ''
    const currentValue = fieldsData[field.unique_name] ?? ''

    return (
      <div key={field.id} className={rowClassName}>
        <LeadField
          field={field}
          size="small"
          value={currentValue}
          onChange={(val) => updateFieldData(field.unique_name, val)}
          fieldSettings={
            field.type === 'phone_number' &&
            (onOpenVkChatWithLead ||
              onOpenMaxChatWithLead ||
              onOpenTelegramChatWithLead ||
              onOpenWazzupChatWithLead)
              ? buildLeadPhoneFieldSettings({
                  canInteractWithIntegrations:
                    access.canInteractWithIntegrations,
                  lead,
                  field,
                  defaultValue,
                  currentValue,
                  onOpenVkChat: (options) => {
                    onOpenVkChatWithLead?.(lead.id, options)
                  },
                  onOpenMaxChat: onOpenMaxChatWithLead
                    ? (options) => onOpenMaxChatWithLead(lead.id, options)
                    : undefined,
                  onOpenTelegramChat: onOpenTelegramChatWithLead
                    ? (options) => onOpenTelegramChatWithLead(lead.id, options)
                    : undefined,
                  onOpenWazzupChat: onOpenWazzupChatWithLead
                    ? (options) => onOpenWazzupChatWithLead(lead.id, options)
                    : undefined,
                })
              : undefined
          }
          disabled={disabled}
        />
      </div>
    )
  }

  const funnelTooltipTitle = () => {
    if (availableFunnels.length === 0) {
      return 'У заявки нет воронок'
    }

    if (availableFunnels.length === 1) {
      return 'У заявки добавлена только одна воронка'
    }

    return ''
  }

  return (
    <div className={cn(classes.mainInfoBlock, classes.gapM)}>
      <LeadProfileModalBlock
        aria-label="Заявка с воронкой"
        title="Воронка и этап"
        accentColor={funnelBundleAccentStatusColor}
      >
        <div
          className={cn(
            classes.funnelBundleLayout,
            multipleDealsEnabled && classes.funnelBundleLayoutDeals,
          )}
        >
          <div
            className={cn(
              classes.funnelContextPanel,
              multipleDealsEnabled && classes.funnelContextPanelDeals,
            )}
          >
            <div className={classes.funnelContextHeader}>
              <span className={classes.funnelContextBadge}>
                {multipleDealsEnabled ? 'Контекст сделки' : 'Контекст воронки'}
              </span>
              <span className={classes.funnelContextHint}>
                {multipleDealsEnabled
                  ? 'Сначала воронка, затем сделка — от них зависят статус, менеджер и поля ниже'
                  : 'От выбранной воронки зависят статус, менеджер и поля ниже'}
              </span>
            </div>

            <div
              className={cn(
                classes.funnelContextControls,
                multipleDealsEnabled && classes.funnelContextControlsDeals,
              )}
            >
              <div
                className={cn(classes.selectBlock, classes.funnelPrimaryBlock)}
              >
                <div className={classes.selectLabel}>
                  Воронка
                  <span className={classes.primaryHint}>главная</span>
                </div>
                {(() => {
                  const funnelSelectDisabled =
                    !hasFunnels || availableFunnels.length < 2
                  const funnelSelect = (
                    <Select
                      size="small"
                      placeholder={hasFunnels ? 'Воронка' : 'Нет воронки'}
                      className={cn(classes.funnelSelect, classes.primarySelect)}
                      open={isFunnelSelectOpen}
                      onDropdownVisibleChange={
                        handleFunnelDropdownVisibleChange
                      }
                      value={
                        hasFunnels ? selectedFunnelId ?? undefined : undefined
                      }
                      onChange={(value) => {
                        lockSelectDropdownClosed(
                          'funnel',
                          setIsFunnelSelectOpen,
                        )
                        selectFunnel(
                          value !== undefined ? Number(value) : null,
                        )
                      }}
                      options={availableFunnels.map((funnel) => ({
                        value: funnel.id,
                        label: funnel.name,
                      }))}
                      disabled={funnelSelectDisabled}
                    />
                  )
                  const tip = funnelTooltipTitle()
                  if (!funnelSelectDisabled || !tip) return funnelSelect
                  return (
                    <Tooltip
                      title={tip}
                      overlayClassName={classes.tooltipOverlay}
                    >
                      <span className={classes.tooltipTarget}>{funnelSelect}</span>
                    </Tooltip>
                  )
                })()}
              </div>

              {multipleDealsEnabled ? (
                <div
                  className={cn(classes.selectBlock, classes.dealSecondaryBlock)}
                >
                  <div className={classes.selectLabel}>
                    Сделка
                    <span className={classes.dependencyHint}>по воронке</span>
                  </div>
                  <Select
                    size="small"
                    placeholder={
                      dealOptions.length > 0 ? 'Сделка' : 'Нет сделок'
                    }
                    className={cn(classes.dealSelect, classes.secondarySelect)}
                    popupClassName={classes.dealSelectPopup}
                    open={isDealSelectOpen}
                    onDropdownVisibleChange={handleDealDropdownVisibleChange}
                    value={
                      dealOptions.length > 0
                        ? selectedLeadFunnelId ?? undefined
                        : undefined
                    }
                    onChange={(value) => {
                      lockSelectDropdownClosed('deal', setIsDealSelectOpen)
                      selectLeadFunnel(
                        value !== undefined ? Number(value) : null,
                      )
                    }}
                    options={dealOptions}
                    disabled={dealOptions.length < 2}
                  />
                </div>
              ) : null}
            </div>

            {multipleDealsEnabled ? (
              <div className={classes.dealCommentRow}>
                {dealCommentExpanded ? (
                  <>
                    <div className={classes.dealCommentLabelRow}>
                      <div className={classes.selectLabel}>
                        Комментарий
                        <span className={classes.dependencyHint}>по сделке</span>
                      </div>
                      <button
                        type="button"
                        className={classes.dealCommentToggleBtn}
                        onClick={() => setDealCommentExpanded(false)}
                        aria-expanded="true"
                      >
                        Свернуть
                        <UpOutlined
                          className={classes.dealCommentToggleIcon}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      </button>
                    </div>
                    <Input.TextArea
                      size="small"
                      allowClear
                      className={classes.dealCommentInput}
                      placeholder="Заметка к сделке…"
                      value={dealCommentValue}
                      title={dealCommentValue || undefined}
                      autoSize={false}
                      rows={3}
                      onChange={(event) =>
                        setDealCommentValue(event.target.value)
                      }
                      disabled={
                        !access.canEditFields ||
                        !hasFunnels ||
                        !isSelectedFunnelAvailable ||
                        isRefetchingLeadCard
                      }
                    />
                  </>
                ) : (
                  <button
                    type="button"
                    className={classes.dealCommentCollapsed}
                    onClick={() => setDealCommentExpanded(true)}
                    aria-expanded="false"
                    disabled={
                      !access.canEditFields ||
                      !hasFunnels ||
                      !isSelectedFunnelAvailable ||
                      isRefetchingLeadCard
                    }
                  >
                    <span className={classes.selectLabel}>
                      Комментарий
                      <span className={classes.dependencyHint}>по сделке</span>
                    </span>
                    <span
                      className={cn(classes.dealCommentPreview, {
                        [classes.dealCommentPreviewEmpty]:
                          !dealCommentValue.trim(),
                      })}
                      title={dealCommentValue.trim() || undefined}
                    >
                      {dealCommentValue.trim() || 'Добавить заметку…'}
                    </span>
                    <DownOutlined
                      className={classes.dealCommentToggleIcon}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </button>
                )}
              </div>
            ) : null}
          </div>

          <div className={classes.funnelDependentPanel}>
            <div className={classes.funnelDependentLabel}>
              {multipleDealsEnabled
                ? 'Зависит от воронки и сделки'
                : 'Зависит от воронки'}
            </div>
            <div className={classes.funnelBundleGrid}>
              <div className={classes.selectBlock}>
                <div className={classes.selectLabel}>
                  Статус
                  <span className={classes.dependencyHint}>
                    {multipleDealsEnabled ? 'по сделке' : 'по воронке'}
                  </span>
                </div>
                <SelectNewStatus
                  leadId={lead.id}
                  lead={lead}
                  funnelId={selectedFunnelId}
                  placeholder={hasFunnels ? 'Статус' : 'Нет воронки'}
                  className={classes.statusSelect}
                  allowedStatusIds={allowedStatusIds}
                  funnelStatuses={selectedFunnelStatuses}
                  skipCurrentStatusFetch={isRefetchingLeadCard}
                  isFunnelAvailable={isSelectedFunnelAvailable}
                  onStatusChanged={onStatusChanged}
                  deferCommit={deferStatusCommit}
                  onDeferredStatusSync={onFunnelStatusSelectionSync}
                  draftResetKey={draftResetKey}
                  isStatusChangeBlocked={
                    hasFunnels &&
                    isSelectedFunnelAvailable &&
                    committedManagerId === null &&
                    selectedManagerId === null
                  }
                />
              </div>

              {showManager ? (
                <div className={classes.selectBlock}>
                  <div className={classes.selectLabel}>
                    Менеджер
                    <span className={classes.dependencyHint}>
                      {multipleDealsEnabled ? 'по сделке' : 'по воронке'}
                    </span>
                  </div>
                  <Select
                    size="small"
                    showSearch
                    allowClear
                    onClear={() => {
                      onSelectManager(null)
                      onClearManager?.()
                    }}
                    placeholder={
                      hasFunnels ? 'Выберите менеджера' : 'Нет воронки'
                    }
                    className={classes.managerSelect}
                    value={
                      hasFunnels ? selectedManagerId ?? undefined : undefined
                    }
                    onChange={(value) =>
                      onSelectManager(
                        value === undefined || value === null || value === ''
                          ? null
                          : Number(value) || null,
                      )
                    }
                    options={managerOptions}
                    disabled={!hasFunnels || !canEditManager}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </LeadProfileModalBlock>

      <LeadProfileModalBlock
        aria-label="Основные поля лида"
        title="Основные поля"
      >
        <div className={cn(classes.mainProfileFields, compactFieldClasses.root)}>
          <div className={classes.mainProfileFieldRowLead}>
            <div className={classes.nextContactField}>
              <div className={classes.selectLabel}>
                Дата следующего контакта
                <span className={classes.dependencyHint}>
                  {multipleDealsEnabled ? 'по сделке' : 'по воронке'}
                </span>
              </div>
              <LeadField
                field={FUNNEL_NEXT_CONTACT_FIELD}
                size="small"
                value={funnelNextContactValue}
                onChange={setFunnelNextContactValue}
                placeholder="Выберите дату и время"
                tooltipTitle={
                  multipleDealsEnabled
                    ? 'Дата следующего контакта по выбранной сделке.'
                    : 'Дата следующего контакта по выбранной воронке.'
                }
                disabled={
                  !access.canEditFields ||
                  !hasFunnels ||
                  !isSelectedFunnelAvailable ||
                  isRefetchingLeadCard
                }
              />
            </div>
          </div>
          {MAIN_PROFILE_LEAD_FIELD_IDS.map((uniqueName) => {
            const field = pickMainProfileField(fields, uniqueName)
            if (!field) return null
            return renderMainProfileField(field, classes.mainProfileFieldRowLead)
          })}
          {MAIN_PROFILE_PARENT_FIELD_IDS.map((uniqueName) => {
            const field = pickMainProfileField(fields, uniqueName)
            if (!field) return null
            return renderMainProfileField(field, classes.mainProfileFieldRowParent)
          })}
          <div className={classes.messengerLinksSection}>
            <VkLinkField
              fields={fields}
              value={fieldsData.VK_LINK ?? ''}
              onChange={(val) => {
                onClearIdentityFieldError?.('VK_LINK')
                updateFieldData('VK_LINK', val)
              }}
              disabled={!access.canEditFields}
              vkIntegration={vkIntegration}
              error={identityFieldErrors?.VK_LINK}
            />
            <MessengerAccountLinkField
              uniqueName={TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME}
              fields={fields}
              value={fieldsData.TELEGRAM_ACCOUNT ?? ''}
              onChange={(val) => {
                onClearIdentityFieldError?.(TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME)
                updateFieldData(TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME, val)
              }}
              disabled={!access.canEditFields}
              integrationConnected={integrationProviders.telegram.canUseLeadChat}
              integrationLoading={isIntegrationAvailabilityLoading}
              error={identityFieldErrors?.TELEGRAM_ACCOUNT}
            />
            <MessengerAccountLinkField
              uniqueName={MAX_ACCOUNT_FIELD_UNIQUE_NAME}
              fields={fields}
              value={fieldsData.MAX_ACCOUNT ?? ''}
              onChange={(val) => {
                onClearIdentityFieldError?.(MAX_ACCOUNT_FIELD_UNIQUE_NAME)
                updateFieldData(MAX_ACCOUNT_FIELD_UNIQUE_NAME, val)
              }}
              disabled={!access.canEditFields}
              integrationConnected={integrationProviders.max.canUseLeadChat}
              integrationLoading={isIntegrationAvailabilityLoading}
              error={identityFieldErrors?.MAX_ACCOUNT}
            />
          </div>
        </div>
      </LeadProfileModalBlock>
    </div>
  )
}

export default MainInfoBlock
